import React from 'react';
import Icon, { IconName } from './Icon';
import { buttonVariants, ButtonVariant } from '../config/colors';
import Loader from '../pages/chat/components/Loader';

export enum ButtonSize {
  xsmall = 'xsmall',
  small = 'small',
  mediumSmall = 'mediumSmall',
  medium = 'medium',
  large = 'large',
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize | null;
  icon?: IconName;
  iconSize?: number;
  iconColor?: string;
  children: React.ReactNode;
  disabled?: boolean;
  href?: string;
  target?: string;
  loading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = ButtonVariant.primary,
  size = ButtonSize.large,
  icon,
  iconSize = 24,
  iconColor,
  children,
  className = '',
  disabled = false,
  href,
  target,
  loading = false,
  ...props
}) => {
  const variantStyles = buttonVariants[variant];

  const classNameWidth = size === ButtonSize.large ? 'w-full' : '';

  const ButtonContent = (
    <button
      className={`
        ${size === ButtonSize.xsmall ? 'px-2 py-1 text-sm' : ''}
        ${size === ButtonSize.small ? 'px-4 py-1 text-sm' : ''}
        ${size === ButtonSize.mediumSmall ? 'px-4 py-1 text-md' : ''}
        ${size === ButtonSize.medium ? 'h-[40px] px-4 text-sm' : ''}
        ${size === ButtonSize.large ? 'h-[49px] px-6' : ''}
        ${classNameWidth}
        ${variantStyles.background}
        ${variantStyles.hover}
        ${variantStyles.text}
        ${disabled ? variantStyles.disabled : ''}
        flex flex-col items-center justify-center 
        font-medium text-lg 
        transition-colors
        rounded-full
        ${className}
      `}
      disabled={disabled || loading}
      {...props}
    >
      <div className={`flex items-center justify-center gap-2 ${loading ? 'h-[1px] invisible' : ''}`}>
        {icon && <Icon name={icon} size={iconSize} color={iconColor} />}
        {typeof children === 'string' ? <span>{children}</span> : children}
      </div>
      {loading && <Loader small />}
    </button>
  );

  if (href && !disabled) {
    return (
      <a 
        href={href} 
        target={target}
        className={classNameWidth}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      >
        {ButtonContent}
      </a>
    );
  }

  return ButtonContent;
};